import { type ReactNode, useState } from 'react'
import { formatDateTime } from '../../language/text.js'
import { word } from '../../language/words.js'
import { RequiredMark } from '../RequiredMark.js'
import type { ConsentProps } from './kinds.js'

/**
 * Het akkoord per persoon (besluit van de product owner van 3 november 2026, eisenboek 12.56, ontwerp 6.2).
 *
 * De verklaring, het eigen vinkje voor wie de sectie mag bewerken, en de lijst van wie akkoord gaf, met
 * het e-mailadres uit het token als naam, het moment, en of het akkoord nog over de inhoud van nu gaat. Er
 * is geen lijst van wie nog moet: wie akkoord kan geven, staat nergens vast. Een akkoord dat vervallen is
 * na een wijziging, staat er nog, met "vervallen na een wijziging": wie het gaf ziet dat hij het opnieuw
 * moet geven, en de anderen zien dat het niet meer telt.
 *
 * **Het vinkje schrijft niet in het document.** Aanvinken en uitvinken vragen het de pod (`onGeven`,
 * `onIntrekken`), en die zet persoon, e-mailadres, moment en stand zelf (R-154). Tot het antwoord er is,
 * staat het vinkje stil en zegt de statusregel dat het wordt verwerkt; een weigering staat er als melding.
 *
 * **Toegankelijk (R-139, R-140).** Een echt selectievakje met de verklaring en "Ik ga akkoord" als naam,
 * de telling en de eigen stand in een live region die er altijd is, de lijst als lijst, en de stand per
 * persoon in woorden, nooit alleen als kleur. Op een telefoon past alles in de breedte: een e-mailadres
 * mag breken.
 */
export function ConsentPerPerson(props: ConsentProps): ReactNode {
  const { veld: field, koppeling: link, taal: language, akkoorden: entries, eigen: own } = props
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | undefined>(undefined)
  const textId = `${link.id}-tekst`
  const statusId = `${link.id}-status`
  const listId = `${link.id}-lijst`
  const valid = entries.filter((entry) => entry.geldig).length
  const mayChoose = props.bewerkbaar && props.magGeven && props.onGeven !== undefined && props.onIntrekken !== undefined

  const choose = async (give: boolean): Promise<void> => {
    if (props.onGeven === undefined || props.onIntrekken === undefined) return
    setBusy(true)
    setFailure(undefined)
    try {
      await (give ? props.onGeven() : props.onIntrekken())
    } catch (error) {
      setFailure(error instanceof Error && error.message !== '' ? error.message : word('akkoordMislukt', language))
    } finally {
      setBusy(false)
    }
  }

  const ownSentence =
    own === undefined ? undefined : own.geldig ? word('akkoordUwGeldig', language) : word('akkoordUwVervallen', language)

  return (
    <div className="ef-schakelaar ef-akkoordlijst" data-akkoord="ja" id={mayChoose ? undefined : link.id}>
      <span className="ef-schakelaar__tekst ef-akkoordlijst__verklaring" id={textId}>
        {field.label !== undefined ? field.label[language] : word('akkoordVink', language)}
      </span>
      {mayChoose ? (
        <label className="ef-schakelaar__regel">
          <input
            type="checkbox"
            className="ef-schakelaar__vinkje"
            {...link}
            aria-labelledby={`${textId} ${link.id}-ik`}
            aria-describedby={[link['aria-describedby'], statusId].filter((part) => part !== undefined).join(' ')}
            checked={own?.geldig === true}
            disabled={busy}
            onChange={(event) => void choose(event.target.checked)}
          />
          <span className="ef-schakelaar__tekst" id={`${link.id}-ik`}>
            {word('akkoordIkGa', language)}
          </span>
          {link['aria-required'] === true ? <RequiredMark /> : null}
        </label>
      ) : null}
      <p className="ef-schakelaar__stempel" role="status" id={statusId}>
        {busy ? word('akkoordBezig', language) : failure !== undefined ? failure : [ownSentence, countSentence(entries.length, valid, language)].filter((part) => part !== undefined).join(' ')}
      </p>
      {entries.length === 0 ? null : (
        <ul className="ef-akkoordlijst__lijst" id={listId} aria-label={word('akkoordLijst', language)}>
          {entries.map((entry) => (
            <li key={entry.sleutel} className="ef-akkoordlijst__regel" data-geldig={entry.geldig ? 'ja' : 'nee'}>
              <span className="ef-akkoordlijst__naam">
                {entry.eigen ? `${word('akkoordU', language)} (${entry.naam})` : entry.naam}
                {entry.namens ? `, ${word('akkoordNamens', language)}` : ''}
              </span>{' '}
              <span>
                {word('akkoordGegevenOp', language)} <time dateTime={entry.gegevenOp}>{formatDateTime(entry.gegevenOp, language, { dateStyle: 'long', timeStyle: 'short' })}</time>
                {entry.geldig ? '' : `, ${word('akkoordVervallen', language)}`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function countSentence(total: number, valid: number, language: 'nl' | 'en'): string {
  if (total === 0) return word('akkoordNogNiemand', language)
  if (language === 'nl') {
    const given = total === 1 ? '1 persoon gaf akkoord' : `${String(total)} personen gaven akkoord`
    return valid === total ? `${given}.` : `${given}, ${String(valid)} daarvan over de inhoud van nu.`
  }
  const given = total === 1 ? '1 person gave consent' : `${String(total)} people gave consent`
  return valid === total ? `${given}.` : `${given}, ${String(valid)} of them to the current content.`
}
